import { useState, useEffect } from 'react';
import { Image, Video, X, Folder, Search } from 'lucide-react';
import { apiFetch } from '../../lib/api';

interface MediaAsset {
  id: number;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  folder: string;
  createdAt: string;
}

interface MediaLibraryPickerProps {
  onSelect: (url: string) => void;
  onClose: () => void;
  open: boolean;
  filterType?: 'image' | 'video' | 'all';
}

export default function MediaLibraryPicker({ onSelect, onClose, open, filterType = 'all' }: MediaLibraryPickerProps) {
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'image' | 'video'>(filterType === 'all' ? 'all' : filterType);

  useEffect(() => {
    if (!open) return;
    loadAssets();
  }, [open, activeTab]);

  const loadAssets = async () => {
    setLoading(true);
    const folder = activeTab === 'all' ? '' : activeTab;
    const res = await apiFetch<{ items: MediaAsset[]; total: number }>(`/api/admin/media-library?folder=${folder}&search=${encodeURIComponent(search)}&limit=50`);
    if (res.success && res.data) {
      setAssets(res.data.items);
    }
    setLoading(false);
  };

  const filteredAssets = assets.filter(a => {
    if (filterType === 'image') return a.mimeType.startsWith('image/');
    if (filterType === 'video') return a.mimeType.startsWith('video/');
    return true;
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">從媒體庫選擇</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b flex gap-3">
          <div className="flex gap-2">
            {[
              { key: 'all', label: '全部', icon: Folder },
              { key: 'image', label: '圖片', icon: Image },
              { key: 'video', label: '影片', icon: Video },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadAssets()}
              placeholder="搜尋檔案名..."
              className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-gray-200 text-sm"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-8 h-8 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <Image className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>沒有找到媒體檔案</p>
              <p className="text-sm mt-1">請先到「媒體庫」上傳</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {filteredAssets.map(asset => (
                <button
                  key={asset.id}
                  onClick={() => { onSelect(asset.url); onClose(); }}
                  className="group relative aspect-[4/5] rounded-xl overflow-hidden border border-gray-200 hover:border-brand-400 hover:shadow-md transition-all bg-gray-50"
                >
                  {asset.mimeType.startsWith('image/') ? (
                    <img
                      src={asset.url}
                      alt={asset.originalName}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => { (e.target as HTMLImageElement).src = ''; (e.target as HTMLImageElement).parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-8 h-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg></div>'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-xs text-white truncate">{asset.originalName}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
